<template>
    <div>
        <template v-if="isBusy">
            <Preloader wrapperHeight="250px" />
        </template>

        <template v-else>
            <GarantieHeader title="Garantie Auto" />
            <GarantieStepper :currentStep="currentStep" :steps="steps" @update:currentStep="handleStepChange" />

            <form method="POST" name="devis_panne_mecanique_standard" @submit.prevent="onSubmit">
                <div class="row">
                    <div class="col-12 form-card-container">
                        <div class="form-card">
                            <!-- STEP 1 -->
                            <template v-if="currentStep === 1">
                                <div class="row m-0 align-items-center">
                                    <div
                                        :class="(isVehicleSearching || searchVehicleFilled) ? 'col-lg-7 pr-lg-4' : 'col-lg-12'">
                                        <div class="form-card-header">
                                            <h2 class="form-card-title">Informations sur le véhicule à assurer</h2>
                                            <span class="required-notice">(* champs obligatoires)</span>
                                        </div>

                                        <!-- Visible Fields -->
                                        <div class="form-row-grid mb-0">
                                            <div class="form-group">
                                                <label class="form-label">Immatriculation <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <div class="registration-search-wrapper">
                                                        <input type="text" v-model="garantieAuto.immatriculation"
                                                            placeholder="Ex: AF-785-HT" maxlength="10"
                                                            @input="handleImmatriculationInput($event)"
                                                            @keydown.enter.prevent="searchByRegNumberImmediately"
                                                            class="redesign-input registration-input-field"
                                                            :class="{ 'input-error': hasError('immatriculation') }" />
                                                    </div>
                                                    <div v-if="hasError('immatriculation')" class="error-message">
                                                        {{ getError('immatriculation') }}
                                                    </div>
                                                </div>
                                                <div v-if="showManualEntryLink || !garantieAuto.immatriculation"
                                                    class="registration-help">
                                                    <a @click="showManualEntryPopup = true" class="manual-entry-link">
                                                        Je ne connais pas mon immatriculation
                                                    </a>
                                                </div>
                                                <div v-else-if="isManualEntry" class="registration-help">
                                                    <a @click="showManualEntryPopup = true" class="manual-entry-link">
                                                        Modifier les informations du véhicule
                                                    </a>
                                                </div>
                                            </div>
                                            <div class="form-group" v-if="!(isVehicleSearching || searchVehicleFilled)">
                                            </div>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group" style="flex: 1;">
                                                <label class="form-label">Kilométrage <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.km_reel"
                                                        placeholder="Ex: 50000" class="redesign-input"
                                                        @input="handleKmInput($event)"
                                                        :class="{ 'input-error': hasError('km_reel') }" />
                                                    <div v-if="hasError('km_reel')" class="error-message">{{
                                                        getError('km_reel')
                                                        }}</div>
                                                </div>
                                            </div>

                                            <div class="form-group" style="flex: 1;">
                                                <label class="form-label">Date d'effet <span
                                                        class="required">*</span></label>
                                                <div
                                                    class="form-input-container date-input-container currency-datepicker">
                                                    <CustomDatepicker :date.sync="effectiveDateInput"
                                                        :disableDate="true" :has-error="hasError('effective_date')"
                                                        class="redesign-datepicker" />
                                                    <div v-if="hasError('effective_date')" class="error-message">
                                                        {{ getError('effective_date') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Required Hidden Fields (kept in code to satisfy requirement) -->
                                        <div style="display: none;">
                                            <select v-model="garantieAuto.type_vehicule">
                                                <option v-for="item in vehicleTypeOptions" :key="item.value"
                                                    :value="item.value">
                                                    {{ item.name }}
                                                </option>
                                            </select>
                                            <input type="text" v-model="garantieAuto.marque_auto" />
                                            <input type="text" v-model="garantieAuto.modele_porteur" />
                                            <select v-model="garantieAuto.carburant">
                                                <option value="1">Essence/Superéthanol</option>
                                                <option value="2">Diesel</option>
                                                <option value="3">GPL</option>
                                                <option value="4">Electrique</option>
                                                <option value="5">Hybride</option>
                                                <option value="6">Essence/GPL</option>
                                            </select>
                                            <select v-model="garantieAuto.power_kw">
                                                <option value="1">– de 150 Kw</option>
                                                <option value="2">De 150 Kw à 400 Kw</option>
                                                <option value="3">+ de 400 Kw</option>
                                            </select>
                                            <select v-model="garantieAuto.cylindree">
                                                <option value="1">- de 2.5 L</option>
                                                <option value="2">de 2.5 L &agrave; 3.5 L</option>
                                                <option value="3">+ de 3.5 L</option>
                                            </select>
                                            <input type="text" v-model="garantieAuto.puis_fisc" />
                                            <CustomDatepicker :date.sync="circulationDateInput" />
                                            <input type="text" v-model="garantieAuto.num_serie" />
                                        </div>
                                    </div>
                                    <!-- Vehicle Summary Card -->
                                    <div class="col-lg-5" v-if="isVehicleSearching || searchVehicleFilled">
                                        <div v-if="isVehicleSearching" class="vehicle-search-loader">
                                            <Preloader wrapperHeight="250px" />
                                        </div>
                                        <VehicleSummaryCard v-else :details="vehicleSummary" />
                                    </div>
                                </div>

                                <div class="form-card-footer-nav justify-content-center align-items-center">
                                    <div class="eligibility-action-wrapper">
                                        <button type="button" class="btn-primary-outline"
                                            :class="{ 'disabled': (isVehicleTooOld && !isManualEntry) || !searchVehicleFilled }"
                                            :disabled="(isVehicleTooOld && !isManualEntry) || !searchVehicleFilled"
                                            @click="goToNextStep">
                                            Suivant
                                        </button>

                                        <div v-if="isVehicleTooOld" class="eligibility-info-trigger"
                                            title="Véhicule non éligible : voir instructions">
                                            <font-awesome-icon icon="info-circle" />
                                            <div class="eligibility-popover">
                                                <div class="popover-title">
                                                    <font-awesome-icon icon="exclamation-triangle" />
                                                    Informations sur l'éligibilité
                                                </div>
                                                <p class="popover-desc">Les critères du véhicule ne permettent pas de
                                                    souscrire à la garantie choisie.</p>
                                                <p class="popover-desc">
                                                    Pour effectuer une demande de dérogation, veuillez adresser le
                                                    contrôle technique à :
                                                    <a
                                                        href="mailto:servicetechnique@dynassurances.fr">servicetechnique@dynassurances.fr</a>
                                                </p>
                                                <div class="popover-desc mb-1">Informations requises :</div>
                                                <ul class="popover-list">
                                                    <li>Immatriculation du véhicule</li>
                                                    <li>Votre code garage</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- STEP 2 -->
                            <template v-else-if="currentStep === 2">
                                <div class="form-card-header">
                                    <h2 class="form-card-title">Information sur la garantie</h2>
                                    <span class="required-notice">(* champs obligatoires)</span>
                                </div>

                                <div class="form-row-grid">
                                    <label class="form-label col-3">Choisissez une durée <span
                                            class="required">*</span></label>
                                    <div class="redesign-option-group highlighted-options col-8 pr-2">
                                        <button v-for="item in durationOptions" :key="item.value" type="button"
                                            class="option-btn"
                                            :class="{ 'active': garantieAuto.duree == item.value, 'option-btn-error': hasError('duree') }"
                                            @click="garantieAuto.duree = item.value">
                                            {{ item.name }}
                                        </button>
                                    </div>
                                    <div v-if="hasError('duree')" class="error-message">{{ getError('duree') }}
                                    </div>
                                </div>

                                <!-- Plans Section -->
                                <InsurancePlansCarousel :apiUrl="'/auto/calculate'" :plans="availablePlans"
                                    :selectedPlanId="garantieAuto.garantie" :duration="garantieAuto.duree"
                                    :vehicle="garantieAuto" @select-plan="selectPlan"
                                    @go-to-step="currentStep = $event" />

                                <div class="form-card-footer-nav">
                                    <button type="button" class="btn-secondary-outline" @click="currentStep--">
                                        Retour
                                    </button>
                                    <button type="button" class="btn-primary-outline" @click="goToNextStep">
                                        Suivant
                                    </button>
                                </div>
                            </template>

                            <!-- STEP 3 -->
                            <template v-else-if="currentStep === 3">
                                <div class="row step-3-row">
                                    <!-- Form Column -->
                                    <div class="col-12 col-lg-8 col-xl-8 pr-xl-4">
                                        <div class="form-card-header">
                                            <h2 class="form-card-title">Informations sur le propriétaire du véhicule
                                            </h2>
                                            <span class="required-notice">(* champs obligatoires)</span>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group flex-width-sm">
                                                <label class="form-label">Civilité</label>
                                                <div class="form-input-container">
                                                    <select v-model="garantieAuto.civilite" class="redesign-select">
                                                        <option value="0">M</option>
                                                        <option value="1">Mlle</option>
                                                        <option value="2">Mme</option>
                                                        <option value="3">Société</option>
                                                        <option value="4">Mr et Mme</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">
                                                    {{ garantieAuto.civilite == 3 ? 'Prénom du signataire' : 'Prénom' }}
                                                    <span class="required">*</span>
                                                </label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.prenom"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('prenom') }" />
                                                    <div v-if="hasError('prenom')" class="error-message">{{
                                                        getError('prenom')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">
                                                    {{ garantieAuto.civilite == 3 ? 'Nom du signataire' : 'Nom' }}
                                                    <span class="required">*</span>
                                                </label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.nom" class="redesign-input"
                                                        :class="{ 'input-error': hasError('nom') }" />
                                                    <div v-if="hasError('nom')" class="error-message">{{ getError('nom')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <template v-if="garantieAuto.civilite == 3">
                                            <div class="form-row-grid">
                                                <div class="form-group full-width">
                                                    <label class="form-label">Société <span
                                                            class="required">*</span></label>
                                                    <div class="form-input-container">
                                                        <input type="text" v-model="garantieAuto.name_of_signatory"
                                                            class="redesign-input"
                                                            :class="{ 'input-error': hasError('name_of_signatory') }" />
                                                        <div v-if="hasError('name_of_signatory')" class="error-message">
                                                            {{
                                                                getError('name_of_signatory') }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>

                                        <div class="form-row-grid">
                                            <div class="form-group full-width">
                                                <label class="form-label">Adresse <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.adresse"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('adresse') }" />
                                                    <div v-if="hasError('adresse')" class="error-message">{{
                                                        getError('adresse')
                                                    }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group">
                                                <label class="form-label">Code postal <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.cp" maxlength="5"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('cp') }" />
                                                    <div v-if="hasError('cp')" class="error-message">{{ getError('cp')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">Ville <span class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.ville"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('ville') }" />
                                                    <div v-if="hasError('ville')" class="error-message">{{
                                                        getError('ville') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group">
                                                <label class="form-label">Téléphone</label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.tel" maxlength="10"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('tel') }" />
                                                    <div v-if="hasError('tel')" class="error-message">{{ getError('tel')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">Email <span class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="email" v-model="garantieAuto.email"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('email') }" />
                                                    <div v-if="hasError('email')" class="error-message">{{
                                                        getError('email') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                        <div v-if="mechanicClientPayment">
                                            <!-- <div class="form-group full-width">
                                                <label class="form-label">Les coordonnées de votre compte <span
                                                        class="text-muted">(Exemple :
                                                        FR7630006000011234567890189)</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieAuto.iban"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('iban') }" />
                                                    <div v-if="hasError('iban')" class="error-message">{{
                                                        getError('iban') }}
                                                    </div>
                                                </div>
                                            </div> -->
                                            <div class="form-group full-width">
                                                <label class="form-label">Date de naissance</label>
                                                <div
                                                    class="form-input-container date-input-container currency-datepicker">
                                                    <CustomDatepicker :date.sync="birthDateInput" :birthdate="true"
                                                        :has-error="hasError('date_of_birth')"
                                                        class="redesign-datepicker" />
                                                    <div v-if="hasError('date_of_birth')" class="error-message">
                                                        {{ getError('date_of_birth') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Summary Column -->
                                    <div class="col-12 col-lg-4 col-xl-4">
                                        <SelectedPlanSummary :plan="{
                                            name: selectedPlanObject ? selectedPlanObject.name : '',
                                            price: selectedPlanPrice || (selectedPlanObject ? selectedPlanObject.basePrice : 0),
                                            key: selectedPlanObject ? selectedPlanObject.key : ''
                                        }" :duration="selectedDurationName" :features="selectedPlanDetailedFeatures"
                                            :vehicle="vehicleSummary"
                                            :termsUrl="selectedPlanObject ? getUrl(selectedPlanObject) : ''" />
                                    </div>

                                    <div class="form-card-footer-nav col-12">
                                        <button type="button" class="btn-secondary-outline" @click="currentStep--">
                                            Retour
                                        </button>
                                        <!-- :disabled="savedQuote && savedQuote.subscription_date" -->
                                        <button type="submit" class="btn-primary-outline">
                                            VOIR LE DEVIS
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <!-- STEP 4 -->
                            <template v-if="currentStep === 4 || currentStep === 5">
                                <GarantieAutoView @change-step="goToNextStep" :current-step="currentStep"
                                    :custom-class="'p-0 m-0 bg-transparent'" :insurance-data="savedQuote" />
                                <div class="form-card-footer-nav col-12" v-if="currentStep === 4">
                                    <button type="button" class="btn-secondary-outline" @click="currentStep--">
                                        Retour
                                    </button>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </form>

            <ManualVehicleEntryModal :is-visible="showManualEntryPopup" :initial-data="garantieAuto"
                @close="showManualEntryPopup = false" @submit="handleManualEntrySubmit" />
        </template>
    </div>
</template>

<script>
import garantieFieldsLists from "@/mixins/garantieFieldsLists";
import Preloader from "@/components/Preloader";
import { DEBOUNCE } from "@/mixins/helper";
import CustomDatepicker from "@/components/CustomInputs/CustomDatepicker";
import InsurancePlansCarousel from "@/components/Garantie/shared/InsurancePlansCarousel";
import GarantieHeader from "@/components/Garantie/shared/GarantieHeader";
import GarantieStepper from "@/components/Garantie/shared/GarantieStepper";
import ManualVehicleEntryModal from "@/components/Garantie/shared/ManualVehicleEntryModal";
import SelectedPlanSummary from "@/components/Garantie/shared/SelectedPlanSummary";
import VehicleSummaryCard from "@/components/Garantie/shared/VehicleSummaryCard";
import manualVehicleEntry from "@/mixins/manualVehicleEntry";
import {
    INSURANCE_AUTO,
    AUTO_ENGINE,
    AUTO_TYPE,
    AUTO_DURATION
} from "@/constants/insuranceAuto";
import { WARRANTY_DETAILS, getWarrantyFeatures } from "@/constants/warrantyDetails";
import GarantieAutoView from "./GarantieAutoView.vue";

const INITIAL_FIELDS_VALUES = Object.freeze({
    id: "",
    civilite: 0,
    nom: "",
    prenom: "",
    name_of_signatory: "",
    adresse: "",
    cp: "",
    ville: "",
    tel: "",
    email: "",
    iban: "",
    jour_permis: new Date().getDate(), //
    mois_permis: new Date().getMonth() + 1, // set today date
    annee_permis: new Date().getFullYear(), //
    type_vehicule: 0,
    garantie: 0,
    duree: 0,
    marque_auto: "",
    modele_porteur: "",
    cylindree: 0,
    power_kw: 0,
    puis_fisc: "",
    immatriculation: "",
    jour_mise_en_circulation: "",
    mois_mise_en_circulation: "",
    annee_mise_en_circulation: "",
    num_serie: "",
    carburant: 0,
    km_reel: 0,
    immatriculation_ancien_numero: "",
    jour_livraison: 1,
    mois_livraison: 1,
    annee_livraison: new Date().getFullYear(),
    jour_vente: 1,
    mois_vente: 1,
    annee_vente: new Date().getFullYear(),
    jour_prise_effet: "",
    mois_prise_effet: "",
    annee_prise_effet: "",
    assure: 0,
    jour_de_naissance: "",
    mois_de_naissance: "",
    annee_de_naissance: ""
});

export default {
    name: "GarantieAuto",
    components: {
        GarantieHeader,
        GarantieStepper,
        InsurancePlansCarousel,
        SelectedPlanSummary,
        VehicleSummaryCard,
        CustomDatepicker,
        ManualVehicleEntryModal,
        GarantieAutoView,
        Preloader
    },
    mixins: [garantieFieldsLists, manualVehicleEntry],
    data() {
        return {
            steps: [
                {
                    text: 'INFOS VÉHICULE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-car-front" viewBox="0 0 16 16">  <path d="M4 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0m10 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0M6 8a1 1 0 0 0 0 2h4a1 1 0 1 0 0-2zM4.862 4.276 3.906 6.19a.51.51 0 0 0 .497.731c.91-.073 2.35-.17 3.597-.17s2.688.097 3.597.17a.51.51 0 0 0 .497-.731l-.956-1.913A.5.5 0 0 0 10.691 4H5.309a.5.5 0 0 0-.447.276"/>  <path d="M2.52 3.515A2.5 2.5 0 0 1 4.82 2h6.362c1 0 1.904.596 2.298 1.515l.792 1.848c.075.175.21.319.38.404.5.25.855.715.965 1.262l.335 1.679q.05.242.049.49v.413c0 .814-.39 1.543-1 1.997V13.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.338c-1.292.048-2.745.088-4 .088s-2.708-.04-4-.088V13.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.892c-.61-.454-1-1.183-1-1.997v-.413a2.5 2.5 0 0 1 .049-.49l.335-1.68c.11-.546.465-1.012.964-1.261a.8.8 0 0 0-.381-.404l.792-1.848ZM4.82 3a1.5 1.5 0 0 0-1.379.91l-.792 1.847a1.8 1.8 0 0 1-.853.904.8.8 0 0 0-.43.564L1.03 8.904a1.5 1.5 0 0 0-.03.294v.413c0 .796.62 1.448 1.408 1.484 1.555.07 3.786.155 5.592.155s4.037-.084 5.592-.155A1.48 1.48 0 0 0 15 9.611v-.413q0-.148-.03-.294l-.335-1.68a.8.8 0 0 0-.43-.563 1.8 1.8 0 0 1-.853-.904l-.792-1.848A1.5 1.5 0 0 0 11.18 3z"/></svg>`
                },
                {
                    text: 'OFFRE SÉLECTIONNÉE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-currency-euro" viewBox="0 0 16 16">
                            <path d="M4 9.42h1.063C5.4 12.323 7.317 14 10.34 14c.622 0 1.167-.068 1.659-.185v-1.3c-.484.119-1.045.17-1.659.17-2.1 0-3.455-1.198-3.775-3.264h4.017v-.928H6.497v-.936q-.002-.165.008-.329h4.078v-.927H6.618c.388-1.898 1.719-2.985 3.723-2.985.614 0 1.175.05 1.659.177V2.194A6.6 6.6 0 0 0 10.341 2c-2.928 0-4.82 1.569-5.244 4.3H4v.928h1.01v1.265H4v.928z"></path>
                        </svg>`
                },
                {
                    text: 'PROPRIÉTAIRE VÉHICULE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-person-fill" viewBox="0 0 16 16">  <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/></svg>`
                },
                {
                    text: 'SOUSCRIPTION',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/></svg>`
                },
                {
                    text: 'DOCUMENTS GARANTIE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-shield-check" viewBox="0 0 16 16">  <path d="M5.338 1.59a61 61 0 0 0-2.837.856.48.48 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56"/>  <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0"/></svg>`
                }
            ],
            currentStep: 1,
            garantieAuto: {
                ...INITIAL_FIELDS_VALUES,
                duree: AUTO_DURATION.MONTH_12.ID
            },
            errors: {},
            isEdit: false,
            isVehicleSearching: false,
            isBusy: false,
            searchResult: null,
            searchVehicleResult: null,
            isClientSearching: false,
            isSending: false,
            effectiveDateInput: this.moment().toDate(),
            circulationDateInput: "",
            selectedPlanPrice: 0,
            AUTO_ENGINE,
            birthDateInput: "",
            savedQuote: null,
            initialImmatriculation: "",
            plans: [
                {
                    id: 3, // TYPE_STANDARD
                    name: 'STANDARD',
                    url: '/generate-pdf-terms-conditions/auto/standard',
                    basePrice: 189
                },
                {
                    id: 4, // TYPE_RESEAU
                    name: 'RÉSEAU',
                    url: '/generate-pdf-terms-conditions/auto/reseau',
                    basePrice: 249
                },
                {
                    id: 5, // TYPE_MAXI_RESEAU
                    name: 'MAXI RÉSEAU',
                    url: '/generate-pdf-terms-conditions/auto/maxi-reseau',
                    basePrice: 299
                },
                {
                    id: 1, // TYPE_MAXI_PLUS
                    name: 'MAXI PLUS',
                    url: '/generate-pdf-terms-conditions/auto/maxi-plus',
                    basePrice: 349
                },
                {
                    id: 2, // TYPE_PRESTIGE
                    name: 'PRESTIGE',
                    url: '/generate-pdf-terms-conditions/auto/prestige',
                    basePrice: 399
                }
            ],
        };
    },
    computed: {
        existingCustomerFound() {
            return !!this.searchResult;
        },
        existingVehicleFound() {
            return !!this.searchVehicleResult;
        },
        insuranceTypeOptions() {
            return +this.garantieAuto.carburant === AUTO_ENGINE.ELECTRIC.ID
                ? [
                    {
                        name: INSURANCE_AUTO.TYPE_RESEAU.NAME,
                        value: INSURANCE_AUTO.TYPE_RESEAU.ID
                    },
                    {
                        name: INSURANCE_AUTO.TYPE_MAXI_RESEAU.NAME,
                        value: INSURANCE_AUTO.TYPE_MAXI_RESEAU.ID
                    }
                ]
                : [
                    {
                        name: INSURANCE_AUTO.TYPE_STANDARD.NAME,
                        value: INSURANCE_AUTO.TYPE_STANDARD.ID
                    },
                    {
                        name: INSURANCE_AUTO.TYPE_RESEAU.NAME,
                        value: INSURANCE_AUTO.TYPE_RESEAU.ID
                    },
                    {
                        name: INSURANCE_AUTO.TYPE_MAXI_RESEAU.NAME,
                        value: INSURANCE_AUTO.TYPE_MAXI_RESEAU.ID
                    },
                    {
                        name: INSURANCE_AUTO.TYPE_MAXI_PLUS.NAME,
                        value: INSURANCE_AUTO.TYPE_MAXI_PLUS.ID
                    },
                    {
                        name: INSURANCE_AUTO.TYPE_PRESTIGE.NAME,
                        value: INSURANCE_AUTO.TYPE_PRESTIGE.ID
                    }
                ];
        },
        vehicleTypeOptions() {
            return [
                { name: AUTO_TYPE.VP.NAME, value: AUTO_TYPE.VP.ID },
                { name: AUTO_TYPE.VU.NAME, value: AUTO_TYPE.VU.ID },
                { name: AUTO_TYPE["4X4"].NAME, value: AUTO_TYPE["4X4"].ID }
            ];
        },
        durationOptions() {
            let durationOptions = [
                { name: AUTO_DURATION.MONTH_6.NAME, value: AUTO_DURATION.MONTH_6.ID },
                { name: AUTO_DURATION.MONTH_12.NAME, value: AUTO_DURATION.MONTH_12.ID },
                { name: AUTO_DURATION.MONTH_24.NAME, value: AUTO_DURATION.MONTH_24.ID }
            ];

            if (
                +this.garantieAuto.garantie === INSURANCE_AUTO.TYPE_STANDARD.ID ||
                +this.garantieAuto.garantie === INSURANCE_AUTO.TYPE_RESEAU.ID
            ) {
                durationOptions.unshift({
                    name: AUTO_DURATION.MONTH_3.NAME,
                    value: AUTO_DURATION.MONTH_3.ID
                });
            }

            return durationOptions;
        },
        mechanicClientPayment() {
            let client_payment =
                this.$store.state.userInfo && this.$store.state.userInfo.mechanic
                    ? this.$store.state.userInfo.mechanic.client_payment
                    : null;
            return client_payment ? client_payment : "";
        },
        availablePlans() {
            const engineId = +this.garantieAuto.carburant;
            const age = this.vehicleAge;

            return this.insuranceTypeOptions.map(opt => {
                const plan = this.plans.find(p => p.id === opt.value);
                if (!plan) return null;

                const detailedFeatures = getWarrantyFeatures({
                    type: plan.id,
                    engine: engineId,
                    age: age,
                    category: 'AUTO'
                });

                return {
                    ...plan,
                    detailedFeatures
                };
            }).filter(p => !!p);
        },
        selectedPlanObject() {
            let data = this.plans.find(p => p.id === JSON.parse(this.garantieAuto.garantie));
            if (this.garantieAuto.garantie) {
                data = this.plans.find(p => p.id === this.garantieAuto.garantie);
            }
            return data;
        },
        selectedDurationName() {
            const opt = this.durationOptions.find(o => o.value === this.garantieAuto.duree);
            return opt ? opt.name : '';
        },
        selectedPlanDetailedFeatures() {
            const planId = this.garantieAuto.garantie;
            if (!planId) return [];

            const isElectric = +this.garantieAuto.carburant === AUTO_ENGINE.ELECTRIC.ID;
            const age = this.vehicleAge;

            const features = getWarrantyFeatures({
                type: planId,
                engine: isElectric ? 'electric' : 'other',
                age: age,
                category: 'AUTO'
            });

            return features.filter(f => f.title.toLowerCase() !== 'important' && f.title.toLowerCase() !== 'import');
        },
        vehicleAge() {
            if (!this.circulationDateInput) return 0;
            const dateMec = this.moment(this.circulationDateInput);
            const today = this.moment();
            return today.diff(dateMec, 'years');
        },
        isVehicleTooOld() {
            return this.vehicleAge > 14;
        },
        vehicleSummary() {
            let details = [];

            if (this.garantieAuto.immatriculation) {
                details.push({ label: 'Immatriculation', value: this.garantieAuto.immatriculation });
            }

            if (this.garantieAuto.marque_auto) {
                details.push({ label: 'Marque', value: this.garantieAuto.marque_auto });
            }

            if (this.garantieAuto.modele_porteur) {
                details.push({ label: 'Modèle', value: this.garantieAuto.modele_porteur });
            }

            if (this.garantieAuto.puis_fisc) {
                details.push({ label: 'Puissance fiscale', value: this.garantieAuto.puis_fisc + ' CV' });
            }

            if (this.garantieAuto.km_reel) {
                details.push({ label: 'Kilométrage', value: this.garantieAuto.km_reel + ' km' });
            }

            if (this.circulationDateInput) {
                details.push({
                    label: 'Date de 1ère mise en circulation',
                    value: this.moment(this.circulationDateInput).format('DD/MM/YYYY')
                });
            }

            if (this.garantieAuto.num_serie) {
                details.push({
                    label: 'Numéro de série',
                    value: this.garantieAuto.num_serie
                });
            }

            return details;
        }

    },
    watch: {
        'garantieAuto.immatriculation'(val) { if (val) this.clearError('immatriculation'); },
        'garantieAuto.km_reel'(val) { if (val) this.clearError('km_reel'); },
        'garantieAuto.type_vehicule'(val) { if (+val !== 0) this.clearError('type_vehicule'); },
        'garantieAuto.garantie'(val) { if (+val !== 0) this.clearError('garantie'); },
        'garantieAuto.duree'(val) { if (+val !== 0) this.clearError('duree'); },
        'garantieAuto.nom'(val) { if (val) this.clearError('nom'); },
        'garantieAuto.prenom'(val) { if (val) this.clearError('prenom'); },
        'garantieAuto.adresse'(val) { if (val) this.clearError('adresse'); },
        'garantieAuto.cp'(val) { if (val) this.clearError('cp'); },
        'garantieAuto.ville'(val) { if (val) this.clearError('ville'); },
        'garantieAuto.tel'(val) { if (val) this.clearError('tel'); },
        'garantieAuto.email'(val) { if (val) this.clearError('email'); },
        'garantieAuto.name_of_signatory'(val) { if (val) this.clearError('name_of_signatory'); },
        effectiveDateInput(val) { if (val) this.clearError('effective_date'); },
        birthDateInput(val) { if (val) this.clearError('date_of_birth'); },
        '$route'(to, from) {
            // If navigating to the base form (no ID), reset everything
            if (!to.params.id) {
                this.currentStep = 1;
                this.isEdit = false;
                this.searchVehicleFilled = false;
                this.garantieAuto = JSON.parse(JSON.stringify(INITIAL_FIELDS_VALUES));
                // Set default type/plan/duration which are set in data()
                this.garantieAuto.type_vehicule = AUTO_TYPE.VP.ID;
                this.garantieAuto.duree = AUTO_DURATION.MONTH_12.ID;

                this.effectiveDateInput = this.moment().add(1, 'days').toDate();
                this.circulationDateInput = "";
                this.birthDateInput = "";
                this.errors = { errors: {} };
            } else if (to.params.id !== from.params.id) {
                if (this.savedQuote && to.params.id == this.savedQuote.id) return;
                // If ID changed (navigating to another existing quote), reload it
                this.getQuoteInfo();
                this.isEdit = true;
            }
        }
    },
    methods: {
        clearError(field) {
            if (this.errors.errors && this.errors.errors[field]) {
                delete this.errors.errors[field];
                this.errors = { ...this.errors };
            }
        },
        resetVehicleData() {
            this.garantieAuto.marque_auto = "";
            this.garantieAuto.modele_porteur = "";
            this.garantieAuto.num_serie = "";
            this.garantieAuto.carburant = 0;
            this.garantieAuto.cylindree = 0;
            this.garantieAuto.power_kw = 0;
            this.garantieAuto.puis_fisc = "";
            this.garantieAuto.jour_mise_en_circulation = "";
            this.garantieAuto.mois_mise_en_circulation = "";
            this.garantieAuto.annee_mise_en_circulation = "";
            this.circulationDateInput = null;
            this.searchVehicleFilled = false;
            this.searchVehicleResult = null;
            this.showManualEntryLink = false;
        },
        formatImmatriculation(value, event) {
            const isDeleting = event && (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward');
            const cleanValue = (value || "")
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "");

            if (!cleanValue) {
                return "";
            }

            const isNumericStart = /^[0-9]/.test(cleanValue);
            const chunkPattern = isNumericStart ? [3, 3, 2] : [2, 3, 2];
            const maxLength = chunkPattern.reduce((sum, size) => sum + size, 0);
            const limitedValue = cleanValue.slice(0, maxLength);

            let formatted = "";
            let cursor = 0;

            for (let i = 0; i < chunkPattern.length; i++) {
                const size = chunkPattern[i];
                const chunk = limitedValue.slice(cursor, cursor + size);
                if (chunk) {
                    formatted += (formatted ? "-" : "") + chunk;

                    // Proactive dash insertion
                    if (!isDeleting && chunk.length === size && i < chunkPattern.length - 1) {
                        // If we just finished typing a chunk, even if next one isn't started
                        if (limitedValue.length === cursor + size) {
                            formatted += "-";
                        }
                    }
                }
                cursor += size;
            }
            return formatted;
        },
        isValidImmatriculation(value) {
            return /(^[A-Z]{2}-[0-9]{3}-[A-Z]{2}$)|(^[0-9]{3}-[A-Z]{3}-[0-9]{2}$)/.test(value || "");
        },
        handleImmatriculationInput(event) {
            let inputValue = event
                ? event.target.value
                : this.garantieAuto.immatriculation;

            this.garantieAuto.immatriculation = this.formatImmatriculation(inputValue, event);

            // Only trigger search if the value has actually changed from the initial value
            if (this.garantieAuto.immatriculation !== this.initialImmatriculation) {
                if (!this.isValidImmatriculation(this.garantieAuto.immatriculation)) {
                    this.resetVehicleData();
                } else {
                    this.resetVehicleData();
                    this.searchByRegNumber();
                }
            }

            this.clearError('immatriculation');
        },
        handleKmInput(event) {
            let val = event.target.value.replace(/\D/g, '');
            if (val === '') val = '0';
            // Remove leading zeros unless it's just "0"
            if (val.length > 1 && val[0] === '0') val = val.replace(/^0+/, '');
            if (val === '') val = '0';
            this.garantieAuto.km_reel = val;
            this.clearError('km_reel');
        },
        searchByRegNumberImmediately() {
            if (this.isValidImmatriculation(this.garantieAuto.immatriculation)) {
                this.searchByRegNumber();
            } else {
                this.$store.dispatch("showNotification", {
                    text: "Veuillez entrer une immatriculation valide.",
                    type: "warn"
                });
            }
        },
        searchByRegNumber: DEBOUNCE(function () {
            let matched = this.garantieAuto.immatriculation.match(
                /(^([A-Z]{2}-[0-9]{3}-[A-Z]{2})+$)|(^([0-9]{3}-[A-Z]{3}-[0-9]{2})+$)/g
            );

            if (matched) {
                let url = "client/registration",
                    data = {
                        regNumber: this.garantieAuto.immatriculation,
                        auto: true
                    };
                this.isVehicleSearching = true;
                this.apiPost(url, data, false)
                    .then(response => {
                        if (response.data.info) {
                            this.searchVehicleResult = response.data.info;
                            this.isManualEntry = false;
                            this.parseVehicleInfo(this.searchVehicleResult);
                        } else {
                            this.showManualEntryLink = true;
                            this.searchVehicleResult = null;
                            this.showManualEntryPopup = true;
                        }

                        this.isVehicleSearching = false;
                    })
                    .catch(error => {
                        this.isVehicleSearching = false;

                        if (error.response && error.response.status === 422) {
                            let errorMessage = '';
                            if (error.response.data && error.response.data.errors && error.response.data.errors.regNumber && error.response.data.errors.regNumber[0]) {
                                errorMessage = error.response.data.errors.regNumber[0];
                            } else if (error.response.data && error.response.data.message) {
                                errorMessage = error.response.data.message;
                            }

                            if (errorMessage) {
                                if (!this.errors.errors) {
                                    this.$set(this.errors, 'errors', {});
                                }
                                this.$set(this.errors.errors, 'immatriculation', [errorMessage]);
                                this.showManualEntryLink = false;
                                this.searchVehicleResult = null;
                                if (typeof this.searchVehicleFilled !== 'undefined') {
                                    this.searchVehicleFilled = false;
                                }
                                return;
                            }
                        }

                        this.showErrorNotification(error);
                        this.showManualEntryLink = true;
                    });
            } else {
                this.showManualEntryLink = false;
            }
        }, 500),
        searchClient: DEBOUNCE(function (val) {
            if (this.garantieAuto.tel.length > 2) {
                let url = "client/searchOne",
                    data = {
                        tel: this.garantieAuto.tel
                    };
                this.isClientSearching = true;
                this.apiGet(url, data, false)
                    .then(response => {
                        if (response.data.client) {
                            this.searchResult = response.data.client;
                            this.parseClientInfo(this.searchResult);
                            this.searchResult = null;
                        }

                        this.isClientSearching = false;
                    })
                    .catch(error => {
                        this.showErrorNotification(error);
                        this.isClientSearching = false;
                    });
            }
        }, 1000),
        fillTheFields() {
            if (this.searchResult) {
                this.parseClientInfo(this.searchResult);
                this.searchResult = null;
            }
        },
        fillTheFieldsByImmatriculation() {
            if (this.searchVehicleResult) {
                this.parseVehicleInfo(this.searchVehicleResult);
                this.searchVehicleFilled = true;
                // this.searchVehicleResult = null;
            }
        },
        serializeData() {
            return this.garantieAuto;
        },
        onSubmit(event) {
            if (!this.validateStep(3)) return;

            this.garantieAuto.immatriculation = this.garantieAuto.immatriculation.toUpperCase();
            this.isSending = true;
            let url = "garantie/auto";
            if (this.isEdit && this.garantieAuto.id) {
                url = "garantie/auto/" + this.garantieAuto.id + "/edit";
            }

            this.apiPost(url, this.serializeData())
                .then(response => {
                    this.isSending = false;
                    this.errors = {};

                    if (response.data.quote && response.data.status == "success") {
                        if (response.data.calcQuotes)
                            this.$store.dispatch(
                                "updateCalcGaranties",
                                response.data.calcQuotes
                            );
                        this.showSuccessNotification(response.data.message, "");
                        this.savedQuote = response.data.quote;
                        this.garantieAuto.id = response.data.quote.id;
                        this.isEdit = true;
                        this.currentStep = 4;
                        // this.$router.push("/garantie-auto-v2-view/" + response.data.quote.id);
                    }
                })
                .catch(error => {
                    this.isSending = false;

                    const errorData = error.response.data || {};
                    const status = error.response.status;

                    this.errors = errorData;

                    // Handle 422 error
                    if (status === 422) {
                        this.showErrorNotification(
                            "Les données du formulaire sont invalides. Veuillez vérifier toutes les étapes."
                        );
                        return;
                    }

                    // Default error message
                    if (errorData.message) {
                        this.showErrorNotification(errorData.message);
                    } else {
                        this.showErrorNotification("Une erreur est survenue.");
                    }

                    // Specific exception
                    if (
                        errorData.exception === "App\\Exceptions\\PriceErrorException"
                    ) {
                        this.errors.errors = {
                            error: [errorData.message],
                        };
                    }
                });
        },

        changeDurations(duration) {
            this.garantieAuto.duree = duration;
        },

        getQuoteInfo() {
            this.isBusy = true;
            let url = "garantie/" + this.$route.params.id;
            this.apiGet(url)
                .then(response => {
                    if (response.data.quote) {
                        this.savedQuote = response.data.quote;
                        this.parseQuoteInfo(response.data.quote);
                    }
                    this.isBusy = false;
                })
                .catch(error => {
                    this.showErrorNotification(error);
                    console.log(error);

                    //this.$router.push("/");
                });
        },
        parseVehicleInfo(info) {
            /* Car brand */
            if (info.Marque) {
                this.garantieAuto.marque_auto = info.Marque.split(".").join("");
            }

            /* Modele */
            this.garantieAuto.modele_porteur = info.Modele
                ? info.Modele.toLocaleString()
                : null;
            /* Num serie */
            this.garantieAuto.num_serie = info.VIN ? info.VIN : null;

            /* CARBUTANT */
            if (info.Energie) {
                //GAZ+ELEC => HYBRIDE
                let carburantFromApi = info.Energie.toString();
                if (
                    carburantFromApi
                        .toLowerCase()
                        .match(/(gaz|s\.?eth)?\+elec|elec\+\w+/i)
                ) {
                    info.Energie = AUTO_ENGINE.HYBRIDE.NAME;
                    this.garantieAuto.carburant = AUTO_ENGINE.HYBRIDE.ID;
                } else {
                    if (info.Energie === "GAZOLE") info.Energie = AUTO_ENGINE.DIESEL.NAME;
                    if (info.Energie === "ELECTRIC")
                        this.garantieAuto.carburant = AUTO_ENGINE.ELECTRIC.ID;
                    if (info.Energie === "SUPERETHANOL")
                        this.garantieAuto.carburant = AUTO_ENGINE.ESSENCE.ID;
                    if (info.Energie === "ESS+G.P.L." || info.Energie === "GAZOGENE")
                        this.garantieAuto.carburant = AUTO_ENGINE.ESSENCE_GPL.ID;
                }

                let founded = this.list_type_car_carburant.findIndex(item => {
                    return item.toUpperCase() === info.Energie.toUpperCase();
                });

                if (founded > 0) {
                    this.garantieAuto.carburant = founded;
                }
            }

            /* Cylindree */
            if (info.Cylindree) {
                let cylindree = +info.Cylindree;

                if (cylindree > 3500) {
                    this.garantieAuto.cylindree = 3;
                } else if (cylindree >= 2500 && cylindree <= 3500) {
                    this.garantieAuto.cylindree = 2;
                } else if (cylindree < 2500) {
                    this.garantieAuto.cylindree = 1;
                }
            }

            /* PuissanceKW */
            if (info.PuissanceKW || info.PuissanceKW == "0") {
                let PuissanceKW = +info.PuissanceKW;
                if (PuissanceKW === 0) {
                    // on met 1 car c'est la valeur min
                    this.garantieAuto.power_kw = 1;
                }

                if (PuissanceKW > 400) {
                    this.garantieAuto.power_kw = 3;
                } else if (PuissanceKW >= 150 && PuissanceKW <= 400) {
                    this.garantieAuto.power_kw = 2;
                } else if (PuissanceKW < 150) {
                    this.garantieAuto.power_kw = 1;
                }
            }

            /* Car type */
            let genre = info.Genre ? info.Genre.toUpperCase() : undefined,
                genreCG = info.GenreCG ? info.GenreCG.toUpperCase() : undefined,
                propulsion = info.Propulsion
                    ? info.Propulsion.toUpperCase()
                    : undefined;

            if (genre === "CTTE" || genreCG === "CTTE" || genreCG === "VASP") {
                this.garantieAuto.type_vehicule = AUTO_TYPE.VU.ID;
            } else if (propulsion === "AVANT" || propulsion === "ARRIERE") {
                this.garantieAuto.type_vehicule = AUTO_TYPE.VP.ID;
            } else if (propulsion === "4X4") {
                this.garantieAuto.type_vehicule = AUTO_TYPE["4X4"].ID;
            } else if (genre === "VP" || genreCG === "VP") {
                this.garantieAuto.type_vehicule = AUTO_TYPE.VP.ID;
            } else if (genre === "VU" || genreCG === "VU") {
                this.garantieAuto.type_vehicule = AUTO_TYPE.VU.ID;
            } else if (genre === "4X4" || genreCG === "4X4") {
                this.garantieAuto.type_vehicule = AUTO_TYPE["4X4"].ID;
            }

            if (info.PuissFisc) {
                this.garantieAuto.puis_fisc = +info.PuissFisc;
            }

            if (info.DateMec) {
                this.circulationDateInput = new Date(info.DateMec);
            }

            this.searchVehicleFilled = true;
            this.showManualEntryLink = false;
        },
        parseClientInfo(client) {
            this.garantieAuto.civilite = client.civility;
            this.garantieAuto.nom = client.last_name;
            this.garantieAuto.prenom = client.first_name;
            this.garantieAuto.name_of_signatory = client.name_of_signatory;
            this.garantieAuto.adresse = client.address;
            this.garantieAuto.cp = client.zip;
            this.garantieAuto.ville = client.city;
            this.garantieAuto.tel = client.tel;
            this.garantieAuto.email = client.email;
            this.garantieAuto.iban = client.iban;
            this.garantieAuto.id_client = client.id;
            if (client.birthday && client.birthday != "1900-01-01 00:00:00") {
                let birth_day = parseInt(client.birthday.split("-")[2]),
                    birth_month = parseInt(client.birthday.split("-")[1]) - 1,
                    birth_year = parseInt(client.birthday.split("-")[0]);

                this.garantieAuto.jour_de_naissance = +birth_day;
                this.garantieAuto.mois_de_naissance = +birth_month;
                this.garantieAuto.annee_de_naissance = +birth_year;

                this.birthDateInput = new Date(birth_year, birth_month, birth_day);
            }
        },
        handleManualEntrySubmit(data) {
            if (data.immatriculation) {
                let url = "client/registration";
                let reqData = { regNumber: data.immatriculation, auto: true };
                this.isVehicleSearching = true;
                this.apiPost(url, reqData, false)
                    .then(() => {
                        this.isVehicleSearching = false;
                        this.handleManualEntrySubmitGeneric(data, 'garantieAuto');
                    })
                    .catch(error => {
                        this.isVehicleSearching = false;
                        if (error.response && error.response.status === 422) {
                            let errorMessage = '';
                            if (error.response.data && error.response.data.errors && error.response.data.errors.regNumber && error.response.data.errors.regNumber[0]) {
                                errorMessage = error.response.data.errors.regNumber[0];
                            } else if (error.response.data && error.response.data.message) {
                                errorMessage = error.response.data.message;
                            }

                            if (errorMessage) {
                                this.showManualEntryPopup = false;
                                this.garantieAuto.immatriculation = data.immatriculation;
                                if (!this.errors.errors) {
                                    this.$set(this.errors, 'errors', {});
                                }
                                this.$set(this.errors.errors, 'immatriculation', [errorMessage]);
                                this.showManualEntryLink = false;
                                this.searchVehicleResult = null;
                                this.searchVehicleFilled = false;
                                return;
                            }
                        }
                        this.handleManualEntrySubmitGeneric(data, 'garantieAuto');
                    });
            } else {
                this.handleManualEntrySubmitGeneric(data, 'garantieAuto');
            }
        },
        parseQuoteInfo(quote) {
            this.garantieAuto.id = quote.id;

            if (quote.client && quote.client.id) {
                this.parseClientInfo(quote.client);
            }

            if (quote.details.date_allowed) {
                this.garantieAuto.jour_permis = +quote.details.datesParse.day_allowed;
                this.garantieAuto.mois_permis = +quote.details.datesParse.month_allowed;
                this.garantieAuto.annee_permis = +quote.details.datesParse.year_allowed;
            }

            this.garantieAuto.type_vehicule = quote.details.type_vehicle;
            this.garantieAuto.garantie = quote.details.garantie;
            this.garantieAuto.duree = quote.details.duration;
            this.garantieAuto.assure = quote.details.assistance;
            this.garantieAuto.marque_auto = quote.details.car_brand;
            this.garantieAuto.modele_porteur = quote.details.modele_porteur;
            this.garantieAuto.cylindree = quote.details.cylinder;
            this.garantieAuto.power_kw = quote.details.power_kw;
            this.garantieAuto.puis_fisc = quote.details.fiscal_power;
            this.garantieAuto.immatriculation = quote.details.registration;
            this.garantieAuto.carburant = quote.details.car_fuel;
            this.garantieAuto.km_reel = quote.details.km_real;

            this.garantieAuto.jour_mise_en_circulation =
                quote.details.day_circulation;
            this.garantieAuto.mois_mise_en_circulation =
                quote.details.month_circulation;
            this.garantieAuto.annee_mise_en_circulation =
                quote.details.year_circulation;

            this.circulationDateInput = new Date(
                quote.details.year_circulation,
                quote.details.month_circulation - 1,
                quote.details.day_circulation
            );

            this.garantieAuto.num_serie = quote.details.serial_number;
            this.garantieAuto.immatriculation_ancien_numero =
                quote.details.registration_old_number;

            // Store initial immatriculation to avoid redundant API calls on load
            if (this.garantieAuto.immatriculation) {
                this.initialImmatriculation = this.garantieAuto.immatriculation;
            }

            if (quote.details.delivery_date) {
                this.garantieAuto.jour_livraison = +quote.details.datesParse
                    .day_delivery;
                this.garantieAuto.mois_livraison = +quote.details.datesParse
                    .month_delivery;
                this.garantieAuto.annee_livraison = +quote.details.datesParse
                    .year_delivery;
            }

            if (quote.details.sale_date) {
                this.garantieAuto.jour_vente = +quote.details.datesParse.day_sale;
                this.garantieAuto.mois_vente = +quote.details.datesParse.month_sale;
                this.garantieAuto.annee_vente = +quote.details.datesParse.year_sale;
            }

            if (quote.details.effective_date) {
                let effective_day = quote.details.datesParse.day_effective,
                    effective_month = quote.details.datesParse.month_effective - 1,
                    effective_year = quote.details.datesParse.year_effective;

                this.garantieAuto.jour_prise_effet = +effective_day;
                this.garantieAuto.mois_prise_effet = +effective_month;
                this.garantieAuto.annee_prise_effet = +effective_year;

                this.effectiveDateInput = new Date(
                    effective_year,
                    effective_month,
                    effective_day
                );
            }

            // In Edit Mode, verify if the vehicle is in the API to show/hide manual entry link
            if (this.garantieAuto.immatriculation) {
                this.handleImmatriculationInput();
            }
        },
        validateEffectiveDate() {
            if (!this.effectiveDateInput) return false;
            let date = this.moment(this.effectiveDateInput);

            this.garantieAuto.jour_prise_effet = date.format("D");
            this.garantieAuto.mois_prise_effet = date.format("M");
            this.garantieAuto.annee_prise_effet = date.format("YYYY");

            return true;
        },
        resetEffectiveDate() {
            this.garantieAuto.jour_prise_effet = 1;
            this.garantieAuto.mois_prise_effet = 1;
            this.garantieAuto.annee_prise_effet = 1970;
        },
        cleanEffectiveDate() {
            this.garantieAuto.jour_prise_effet = "";
            this.garantieAuto.mois_prise_effet = "";
            this.garantieAuto.annee_prise_effet = "";
        },
        validateCirculationDate() {
            if (!this.circulationDateInput) return false;
            let date = this.moment(this.circulationDateInput);

            this.garantieAuto.jour_mise_en_circulation = date.format("D");
            this.garantieAuto.mois_mise_en_circulation = date.format("M");
            this.garantieAuto.annee_mise_en_circulation = date.format("YYYY");

            return true;
        },
        resetCirculationDate() {
            this.garantieAuto.jour_mise_en_circulation = "";
            this.garantieAuto.mois_mise_en_circulation = "";
            this.garantieAuto.annee_mise_en_circulation = "";
        },
        validateBirthDate() {
            if (!this.birthDateInput) return false;
            let date = this.moment(this.birthDateInput);

            this.garantieAuto.jour_de_naissance = date.format("D");
            this.garantieAuto.mois_de_naissance = date.format("M");
            this.garantieAuto.annee_de_naissance = date.format("YYYY");

            return true;
        },
        resetBirthDate() {
            this.garantieAuto.jour_de_naissance = 1;
            this.garantieAuto.mois_de_naissance = 1;
            this.garantieAuto.annee_de_naissance = 1970;
        },
        cleanBirthDate() {
            this.garantieAuto.jour_de_naissance = "";
            this.garantieAuto.mois_de_naissance = "";
            this.garantieAuto.annee_de_naissance = "";
        },
        hasError(field) {
            return !!(this.errors.errors && this.errors.errors[field]);
        },
        getError(field) {
            if (this.errors.errors && this.errors.errors[field]) {
                return this.errors.errors[field][0];
            }
            return null;
        },
        isValidEmail(email) {
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(email).toLowerCase());
        },
        selectPlan(planId, price) {
            this.garantieAuto.garantie = Number(planId);
            if (price !== undefined) {
                this.selectedPlanPrice = price;
            }
        },
        validateStep(step) {
            this.errors = { errors: {} };
            let isValid = true;

            if (step === 1) {
                if (!this.isManualEntry && !this.garantieAuto.immatriculation) this.$set(this.errors.errors, 'immatriculation', ['Immatriculation requise.']);
                if (!this.garantieAuto.type_vehicule || this.garantieAuto.type_vehicule == 0) this.$set(this.errors.errors, 'type_vehicule', ['Type de véhicule requis.']);
                if (!this.garantieAuto.km_reel) this.$set(this.errors.errors, 'km_reel', ['Kilométrage requis.']);
                if (this.isVehicleTooOld && !this.isManualEntry) this.$set(this.errors.errors, 'eligibility', ["Les critères du véhicule ne permettent pas de souscrire à la garantie choisie."]);
                if (!this.searchVehicleFilled) {
                    this.$set(this.errors.errors, 'immatriculation', ['Veuillez identifier le véhicule (immatriculation ou saisie manuelle).']);
                }

                if (!this.effectiveDateInput) {
                    this.$set(this.errors.errors, 'effective_date', ['Date d\'effet requise.']);
                } else {
                    this.validateEffectiveDate();
                    if (this.circulationDateInput) this.validateCirculationDate();
                }
            } else if (step === 2) {
                if (!this.garantieAuto.garantie || this.garantieAuto.garantie == 0) this.$set(this.errors.errors, 'garantie', ['Garantie requise.']);
                if (!this.garantieAuto.duree || this.garantieAuto.duree == 0) this.$set(this.errors.errors, 'duree', ['Durée requise.']);
            } else if (step === 3) {
                if (!this.garantieAuto.nom) this.$set(this.errors.errors, 'nom', ['Nom requis.']);
                if (!this.garantieAuto.prenom) this.$set(this.errors.errors, 'prenom', ['Prénom requis.']);
                if (!this.garantieAuto.adresse) this.$set(this.errors.errors, 'adresse', ['Adresse requise.']);
                if (!this.garantieAuto.cp) this.$set(this.errors.errors, 'cp', ['Code postal requis.']);
                if (!this.garantieAuto.ville) this.$set(this.errors.errors, 'ville', ['Ville requise.']);
                if (!this.garantieAuto.email) {
                    this.$set(this.errors.errors, 'email', ['Email requis.']);
                } else if (!this.isValidEmail(this.garantieAuto.email)) {
                    this.$set(this.errors.errors, 'email', ["Format d'email invalide."]);
                }
                if (this.garantieAuto.civilite == 3 && !this.garantieAuto.name_of_signatory) {
                    this.$set(this.errors.errors, 'name_of_signatory', ['Nom de la société requis.']);
                }
                this.validateBirthDate();
                this.validateEffectiveDate();
                if (this.circulationDateInput) this.validateCirculationDate();
            }

            if (Object.keys(this.errors.errors).length > 0) {
                isValid = false;
                this.$notify({
                    group: "info",
                    type: "warn",
                    title: "Validation",
                    text: "Veuillez remplir les champs obligatoires."
                });
            }

            return isValid;
        },
        handleStepChange(newStep) {
            if (newStep < this.currentStep) {
                this.currentStep = newStep;
            } else if (newStep > this.currentStep) {
                // To jump ahead, we'd theoretically need to validate all intermediate steps.
                // For now, let's just allow clicking the "next" logical step if valid.
                if (newStep === this.currentStep + 1) {
                    this.goToNextStep();
                    if (this.currentStep == 2) {
                        this.validateEffectiveDate();
                        this.validateCirculationDate();
                        this.validateBirthDate();
                    }
                }
            }
        },
        goToNextStep() {
            if (this.validateStep(this.currentStep)) {
                this.currentStep++;
            }
        },
        getUrl(plan) {
            let apiUrl = this.$store.state.baseUrl;
            return apiUrl + plan.url;
        },
        toggleFeature(index) {
            const pos = this.expandedFeatures.indexOf(index);
            if (pos > -1) {
                this.expandedFeatures.splice(pos, 1);
            } else {
                this.expandedFeatures.push(index);
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        this.garantieAuto = JSON.parse(JSON.stringify(INITIAL_FIELDS_VALUES));
        next();
    },
    mounted() {
        if (this.$route.params.id) {
            this.getQuoteInfo();
            this.isEdit = true;
            this.searchVehicleFilled = true;
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/components/Garantie/auto/GarantieAuto.vue